import {
  pendingTextIdentity,
  containsTextEffect,
  textEffectAcknowledged,
  stableTextIdentityScope,
} from '../state/text-send-identities.ts';
import type { ApiErrorBody, Paginated } from '@shared/types/index.ts';
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, EXPECTED_LOCATION_HEADER_NAME } from '@shared/constants/index.ts';
import i18n from '../i18n/index.ts';
import {
  captureOfflineGetContext,
  captureOfflineMutationContext,
  invalidateOfflineGetsAfterMutation,
  isOfflineNetworkFailure,
  markOfflineFallbackData,
  readOfflineGet,
  recordOfflineFallback,
  recordOfflineLiveResponse,
  recordOfflineRequestFailure,
  retireOfflineGet,
  writeOfflineGet,
} from '../state/offline-cache.ts';

/**
 * Typed API client.
 *
 * Authentication rides on the HttpOnly session cookie — no token is ever kept
 * in Local Storage (spec §6.1). The readable CSRF cookie is echoed back in a
 * header to satisfy the double-submit check on every write.
 */

export const API_BASE = '/api/v1';

/**
 * Called whenever the API reports the session is gone.
 *
 * Without it an expired or revoked session leaves the cached `session` query in
 * place: the app keeps rendering as if signed in while every request fails, and
 * the only way out is a manual sign-out.
 */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/**
 * Called whenever the API answers 402 SUBSCRIPTION_REQUIRED. The lock can
 * close while the app is open (trial ran out, subscription canceled
 * elsewhere); refreshing the session swaps the UI to the lock screen instead
 * of leaving pages that render but cannot save.
 */
let onSubscriptionRequired: (() => void) | null = null;

export function setSubscriptionRequiredHandler(handler: (() => void) | null): void {
  onSubscriptionRequired = handler;
}

/**
 * Called whenever a WRITE is refused with 403 PLAN_REQUIRED — the company's
 * plan tier does not carry the feature. The app shell shows one notice naming
 * the needed plan, so every form does not have to handle the code itself.
 * Reads are excluded on purpose: pages that fetch a gated resource (the
 * dashboard) render their own upgrade panel, and a toast on top would say the
 * same thing twice.
 */
let onPlanRequired: ((requiredPlan: string, message: string) => void) | null = null;

export function setPlanRequiredHandler(handler: ((requiredPlan: string, message: string) => void) | null): void {
  onPlanRequired = handler;
}

/**
 * The location this tab is showing, as the session provider last published it.
 *
 * The working location belongs to the server session, which every tab shares.
 * Sending the one on screen with each write lets the Worker refuse a save from
 * a tab still showing the location another tab has just switched away from
 * (409 LOCATION_CHANGED) instead of filing it under the new one.
 */
let expectedLocationId: string | null = null;

export function setExpectedLocationId(locationId: string | null): void {
  expectedLocationId = locationId;
}

/**
 * Called on 409 LOCATION_CHANGED with the location the refused write claimed.
 * Refreshes the session so this tab moves onto the location the session is
 * really in, and resolves to that location's name — or null when the refresh
 * did not land somewhere else — so the error can say where the page now is.
 */
type LocationChangedHandler = (refusedLocationId: string | null) => Promise<string | null>;
let onLocationChanged: LocationChangedHandler | null = null;

export function setLocationChangedHandler(handler: LocationChangedHandler | null): void {
  onLocationChanged = handler;
}

/**
 * Last-resort wording when a response carries neither our error envelope nor a
 * status text. Names the layer at fault as far as the browser can tell, so the
 * alert is actionable rather than a bare number.
 */
function describeStatus(status: number): string {
  if (status === 0) return i18n.t('errors.network');
  if (status === 401 || status === 403) return i18n.t('errors.forbidden', { status });
  if (status >= 500) return i18n.t('errors.server', { status });
  return i18n.t('errors.generic', { status });
}

/**
 * Worker error codes (see `src/worker/lib/errors.ts`) whose message is the
 * same fixed sentence at every call site, verified by reading how each
 * factory function is actually invoked across the Worker. That fixed-ness is
 * exactly what makes translating by code alone safe here.
 *
 * Codes deliberately left out — `NOT_FOUND`, `FORBIDDEN`, `VALIDATION_ERROR`,
 * `QUOTE_LOCKED`, `INVALID_STATUS_TRANSITION`, `UNSUPPORTED_MEDIA_TYPE`,
 * `QUOTA_EXCEEDED`, `PAYLOAD_TOO_LARGE`, `INTERNAL_ERROR` — are all raised
 * with a message written per call site (naming the specific record, field,
 * role, or limit involved), so a single translation per code would silently
 * replace a more specific message with a generic one. Free-text field
 * validation messages are never touched, by design.
 */
const TRANSLATABLE_ERROR_CODES = new Set([
  'UNAUTHENTICATED',
  'INVALID_CREDENTIALS',
  'CSRF_FAILED',
  'RATE_LIMITED',
  'TURNSTILE_FAILED',
  'SUBSCRIPTION_REQUIRED',
  'QUOTE_VERSION_CONFLICT',
  'LOCATION_CHANGED',
  'VISIT_INSTRUCTIONS_ACK_REQUIRED',
  'AVAILABILITY_CONFIRMATION_REQUIRED',
]);

/** The translated message for a recognized stable code, or `fallback` otherwise. */
function localizedErrorMessage(code: string | undefined, fallback: string): string {
  if (!code || !TRANSLATABLE_ERROR_CODES.has(code)) return fallback;
  const key = `errors.code.${code}`;
  const translated = i18n.t(key);
  // Guards a key that somehow went missing from the locale files — i18next's
  // default behavior for a missing key is to hand back the key itself.
  return translated === key ? fallback : translated;
}

/**
 * Parses the HTTP Retry-After header into a conservative whole-second delay.
 * Both RFC delay-seconds and HTTP-date values are accepted. A valid value is
 * never exposed as zero: operation-preserving callers need a real pause before
 * replaying the same durable request.
 */
function retryAfterSeconds(value: string | null | undefined, now = Date.now()): number | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isSafeInteger(seconds) ? Math.max(1, seconds) : undefined;
  }

  const at = Date.parse(trimmed);
  if (!Number.isFinite(at)) return undefined;
  return Math.max(1, Math.ceil((at - now) / 1_000));
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  /**
   * The message must never come out empty. `statusText` is the obvious
   * fallback and is always `''` over HTTP/2, so any response that is not our
   * JSON envelope — an edge 403, a WAF block, a 502 from in front of the
   * Worker — used to produce an error carrying no text at all. Every form
   * renders its alert as `{error ? <Alert/> : null}`, so that showed the user
   * nothing whatsoever: the button stops spinning and the page just sits
   * there. `||` rather than `??` so an empty string falls through too.
   *
   * Most Worker failures still arrive in English — the envelope's own
   * `message`/`fields` are written server-side, which does not know the
   * browser's locale, and translating free text is out of scope here. The one
   * exception is the small set of error codes above whose message never
   * varies: those are translated client-side before anything else gets a say.
   */
  constructor(status: number, body: ApiErrorBody | null, fallbackMessage: string, retryAfter?: string | null) {
    const rawMessage = body?.error?.message || fallbackMessage || describeStatus(status);
    const code = body?.error?.code;
    const fields = body?.error?.fields ?? {};
    super(
      code === 'CHECKLIST_PHOTOS_MISSING'
        ? i18n.t('checklists.missingPhotos', { count: Number(fields.missingRequiredPhotos) || 1 })
        : code === 'CHECKLIST_ORDER_LOCKED'
          ? i18n.t('checklists.orderLocked', { task: fields.taskName ?? '' })
          : code === 'CHECKLIST_SNAPSHOT_CHANGED'
            ? i18n.t('checklistStack.snapshotChanged')
            : code === 'CHECKLIST_SNAPSHOT_PENDING'
              ? i18n.t('checklistStack.pending')
              : localizedErrorMessage(code, rawMessage),
    );
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.fields = body?.error?.fields ?? {};
    this.requestId = body?.requestId;
    this.retryAfterSeconds = retryAfterSeconds(retryAfter);
  }

  get isVersionConflict(): boolean {
    return this.code === 'QUOTE_VERSION_CONFLICT';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * Lets the help assistant's streaming transport (api/help.ts) report a dead
 * session the way every `apiRequest` 401 does — the panel must not keep a
 * signed-in shell rendering around a session that is gone.
 */
export function reportUnauthenticated(): void {
  onUnauthenticated?.();
}

function readCsrfCookie(): string | null {
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index === -1) continue;
    if (part.slice(0, index).trim() === CSRF_COOKIE_NAME) return part.slice(index + 1).trim();
  }
  return null;
}

export interface RequestOptions {
  /** Reuse for an explicit retry of one logical send. */
  idempotencyKey?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** FormData bodies skip JSON encoding so the browser sets the boundary. */
  formData?: FormData;
  signal?: AbortSignal;
  /** A conflict preflight must prove it reached the server, not use IndexedDB. */
  offlineFallback?: boolean;
}

/** Cache maintenance must never replace the server's real result or error. */
async function ignoreOfflineCacheFailure(operation: () => void | Promise<void>): Promise<void> {
  try {
    await operation();
  } catch {
    // IndexedDB/localStorage are optional browser enhancements.
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  // Scope is captured before the request leaves. A sign-out or location
  // switch while it is in flight invalidates this context, so the response
  // cannot cross into the next session's cache.
  const offlineContext = method === 'GET' && options.offlineFallback !== false ? captureOfflineGetContext(path) : null;
  const offlineMutationContext = method === 'GET' ? null : captureOfflineMutationContext(path, method);
  const pendingText =
    method === 'POST' && offlineMutationContext && containsTextEffect(path, options.body)
      ? await pendingTextIdentity(stableTextIdentityScope(offlineMutationContext.scope), path, options.body)
      : null;
  const headers: Record<string, string> = { accept: 'application/json' };
  // Captured with the request, like the offline scope above: the claim is
  // about what was on screen when the write was made.
  const claimedLocationId = method === 'GET' ? null : expectedLocationId;

  if (method !== 'GET') {
    headers['Idempotency-Key'] = pendingText?.id ?? options.idempotencyKey ?? crypto.randomUUID();
    const csrf = readCsrfCookie();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
    if (claimedLocationId) headers[EXPECTED_LOCATION_HEADER_NAME] = claimedLocationId;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
  };
  if (body !== undefined) init.body = body;
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, init);
  } catch (error) {
    // A rejected write is ambiguous: the server may have committed before the
    // response connection disappeared (or before an abort reached it). Retire
    // every representation that could predate that write before exposing the
    // failure, so a later offline GET cannot resurrect the old state.
    if (offlineMutationContext) {
      await ignoreOfflineCacheFailure(() => invalidateOfflineGetsAfterMutation(offlineMutationContext));
    }
    // Only a rejected fetch is an offline fallback. An HTTP 401, 404, 409 or
    // 5xx is a real server answer and continues through the ordinary error
    // path below; an AbortError continues to cancel the query as requested.
    if (offlineContext && isOfflineNetworkFailure(error)) {
      const cached = await readOfflineGet<T>(offlineContext);
      if (cached) {
        recordOfflineFallback(offlineContext, cached.cachedAt);
        return markOfflineFallbackData(cached.data, offlineContext, cached.cachedAt);
      }
    }
    recordOfflineRequestFailure(offlineContext);
    throw error;
  }

  // Any mutation response can be ambiguous at an intermediary — especially a
  // 5xx returned after an upstream committed. Conservatively fence related
  // fallbacks for every live write outcome. A definitive 4xx may evict data
  // unnecessarily, but can never make an older offline answer look current.
  if (offlineMutationContext) {
    await ignoreOfflineCacheFailure(() => invalidateOfflineGetsAfterMutation(offlineMutationContext));
  }

  if (response.status === 204) {
    recordOfflineLiveResponse(offlineContext);
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const error = new ApiRequestError(
      response.status,
      payload as ApiErrorBody | null,
      response.statusText,
      response.headers.get('retry-after'),
    );
    // An HTTP error did not replace any cached page, but it is terminal for
    // ordering purposes so older in-flight completions cannot win afterward.
    recordOfflineRequestFailure(offlineContext);
    // A definitive live answer supersedes an older exact fallback. In
    // particular, a resource that is now forbidden or gone must not reappear
    // merely because the following request loses its network connection.
    if (method === 'GET' && (error.status === 401 || error.status === 403 || error.status === 404)) {
      await ignoreOfflineCacheFailure(() => retireOfflineGet(offlineContext));
    }
    // The session endpoint answers 401 for an ordinary signed-out visitor, so
    // it is not a session that just went away.
    if (error.isUnauthenticated && path !== '/auth/session') onUnauthenticated?.();
    if (error.status === 402 && error.code === 'SUBSCRIPTION_REQUIRED') onSubscriptionRequired?.();
    if (method !== 'GET' && error.status === 403 && error.code === 'PLAN_REQUIRED') {
      onPlanRequired?.(error.fields.requiredPlan ?? '', error.message);
    }
    // The location was switched in another tab and nothing was saved. Move
    // this tab onto the session's real location before the caller shows the
    // error, so the sentence can name where the page now is — and never let a
    // failed refresh turn the refusal into something that reads as success.
    if (error.status === 409 && error.code === 'LOCATION_CHANGED') {
      let locationName: string | null = null;
      try {
        locationName = (await onLocationChanged?.(claimedLocationId)) ?? null;
      } catch {
        locationName = null;
      }
      if (locationName) error.message = i18n.t('errors.locationChangedTo', { location: locationName });
    }
    throw error;
  }

  // A 2xx that is not the JSON envelope — an edge proxy answering HTML, a
  // bare `{}`, an array — must fail like every other bad answer, not return
  // `undefined` as a "successful" result that only blows up downstream. The
  // envelope is `{ data: ... }`, so that is what is required.
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload) || !('data' in payload)) {
    recordOfflineRequestFailure(offlineContext);
    throw new ApiRequestError(response.status, null, response.statusText);
  }
  const data = (payload as { data: T }).data;
  recordOfflineLiveResponse(offlineContext);
  // Do not add IndexedDB latency to the successful request. The cache is a
  // best-effort enhancement and handles quota/private-mode failures itself.
  if (offlineContext) void writeOfflineGet(offlineContext, data);
  if (pendingText && textEffectAcknowledged(path, data))
    await ignoreOfflineCacheFailure(() => pendingText.acknowledge());
  return data;
}

/** A file fetched as bytes, with whatever name the server asked it be saved as. */
export interface DownloadedFile {
  blob: Blob;
  /** From `content-disposition`, or null when the response did not name one. */
  filename: string | null;
}

/**
 * Reads `filename` out of a `content-disposition` header.
 *
 * The RFC 5987 spelling (`filename*=UTF-8''…`) wins when both are present, as
 * the spec requires — it is the one that can carry non-ASCII, and a server
 * sending both means the plain `filename` is the lossy fallback.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape is not worth failing a download over; fall
      // through to the plain spelling below.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  const value = plain?.[2] ?? plain?.[1];
  return value ? value.trim() : null;
}

/**
 * Fetches a binary response (a PDF, an export) as a blob instead of navigating
 * to it.
 *
 * A plain `<a href>` hands the URL to the browser and tells the app nothing: a
 * PDF that takes ten seconds to render looks like a click that did nothing, and
 * a failure arrives as a raw JSON error page in a new tab. Pulling the bytes
 * here means the caller can show progress while it runs, and errors come back
 * through the same envelope — and the same session/subscription handlers — as
 * every other request.
 *
 * `url` is a full same-origin path (`/api/v1/…`), not an API-relative one: the
 * endpoints these hit already publish complete URLs.
 */
export async function apiDownload(url: string, signal?: AbortSignal): Promise<DownloadedFile> {
  const init: RequestInit = { method: 'GET', credentials: 'same-origin' };
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    const payload = isJson ? await response.json().catch(() => null) : null;
    const error = new ApiRequestError(
      response.status,
      payload as ApiErrorBody | null,
      response.statusText,
      response.headers.get('retry-after'),
    );
    if (error.isUnauthenticated) onUnauthenticated?.();
    if (error.status === 402 && error.code === 'SUBSCRIPTION_REQUIRED') onSubscriptionRequired?.();
    throw error;
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('content-disposition')),
  };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(path, signal ? { method: 'GET', signal } : { method: 'GET' }),
  /** A real network read, used immediately before replaying a durable write. */
  getFresh: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(
      path,
      signal ? { method: 'GET', signal, offlineFallback: false } : { method: 'GET', offlineFallback: false },
    ),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    apiRequest<T>(path, { method: 'POST', body, idempotencyKey }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, formData: FormData) => apiRequest<T>(path, { method: 'POST', formData }),
};

/**
 * Walks every page of a list endpoint and returns them as one page.
 *
 * For the handful of places that genuinely need a complete list rather than a
 * screenful: the room and task pickers in the quote editor, and the property
 * picker. Those render a chooser, and a chooser missing its last entries is
 * worse than a slow one — the estimator cannot tell that the thing they are
 * looking for exists but was left out.
 *
 * The server refuses to answer any single request with more than
 * `MAX_PAGE_SIZE` rows; this is the client agreeing to ask more than once,
 * which is the difference between a bounded query and a bounded answer.
 *
 * ## Why this is more careful than a loop
 *
 * A multi-request walk over `LIMIT`/`OFFSET` pages is not atomic. If somebody
 * reorders or deletes a row between two of the requests, the window shifts
 * underneath: a row already collected comes back on a later page while another
 * slides past unread. Counting rows as they arrive would then reach the
 * reported total and call that proof of a complete list, which is exactly the
 * silent short list this function exists to prevent.
 *
 * So it does three things instead. It collects by id, which makes a row
 * arriving twice count once rather than papering over a gap. It abandons an
 * attempt the moment the reported total changes underneath it, because the
 * count the walk is about to be judged against is no longer the count the walk
 * started from. And it treats a short result as failure rather than as an
 * answer: if the unique count comes up under the total, the walk is restarted,
 * and if it will not settle the call throws. Complete, or an error — never
 * quietly short.
 *
 * ## What this still does not catch
 *
 * A pair of writes that cancel out in the total — one row deleted before the
 * offset already read and one added after it — leaves every page length and
 * every reported total exactly as a settled list would, while the window has
 * shifted by one. Nothing observable from the client separates that from a
 * clean read, so this returns it. Only paging that cannot shift closes it:
 * the export's cursor walk (`collectByCursor`), or a snapshot/revision token
 * from the server. Matching that here means cursor parameters on these three
 * endpoints, whose orderings (`display_order`, category, name) are not the id
 * a cursor would key on. Said plainly rather than implied away: the checks
 * below narrow the window, they do not eliminate it.
 */
export async function fetchAllPages<T extends { id: string }>(
  fetchPage: (page: number, pageSize: number) => Promise<Paginated<T>>,
  { pageSize = 100, maxPages = 50, attempts = 3 } = {},
): Promise<Paginated<T>> {
  let instability = 'it kept changing while being read';

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const byId = new Map<string, T>();
    let total: number | null = null;
    let exhausted = false;
    let shifted = false;

    for (let page = 1; page <= maxPages; page += 1) {
      const result = await fetchPage(page, pageSize);

      // The total is the yardstick the collected count is measured against, so
      // a walk whose yardstick moved mid-flight cannot be checked at all. With
      // 101 rows and pages of 100, deleting one row before the offset makes
      // page 2 come back empty reporting 100 — a hundred ids against a total
      // of a hundred, which the completeness check below would wave through
      // while holding the deleted row and missing the live one behind it.
      if (total !== null && result.total !== total) {
        shifted = true;
        instability = `the list changed size while being read (${total} then ${result.total} items)`;
        break;
      }
      total = result.total;

      for (const item of result.items) byId.set(item.id, item);

      // A page shorter than asked for is the end of the list. Reaching the
      // reported total is not enough on its own when that total moved mid-
      // walk — see the note above. A walk that fills the page budget at a
      // *stable* total is complete: there is no next page to ask for, and
      // treating that as "still changing" would refuse every list that is
      // exactly `maxPages × pageSize` long.
      if (result.items.length < pageSize) {
        exhausted = true;
        break;
      }
    }

    const items = [...byId.values()];
    const reachedTotal = items.length >= (total ?? 0);
    const filledBudget = items.length >= maxPages * pageSize;
    if (!shifted && reachedTotal && (exhausted || filledBudget)) {
      return {
        items,
        page: 1,
        pageSize: items.length,
        total: total ?? items.length,
        totalPages: 1,
      };
    }

    if (!shifted) instability = `it kept changing while being read (${items.length} of ${total ?? 0} items)`;
  }

  throw new Error(`Could not load the full list: ${instability}.`);
}

/** Builds a query string, omitting empty values so URLs stay tidy. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}
